/**
 * analytics.js — Centralized analytics for GA4, Mixpanel, and Sentry
 */

// ── Google Analytics 4 ───────────────────────────────────────────
const GA4_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID;

export function initGA4() {
  if (!GA4_ID) return;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GA4_ID, { send_page_view: false });
}

export function trackPageView(path) {
  if (!window.gtag) return;
  window.gtag("event", "page_view", { page_path: path });
}

export function trackEvent(eventName, params = {}) {
  if (window.gtag) {
    window.gtag("event", eventName, params);
  }
  if (window.mixpanel) {
    window.mixpanel.track(eventName, params);
  }
}

// Predefined conversion events
export const events = {
  signup:            (method) => trackEvent("sign_up",           { method }),
  login:             (method) => trackEvent("login",             { method }),
  searchSalon:       (query)  => trackEvent("search",            { search_term: query }),
  viewSalon:         (slug)   => trackEvent("view_salon",        { salon_slug: slug }),
  viewStylist:       (id)     => trackEvent("view_stylist",      { stylist_id: id }),
  beginBooking:      (salon)  => trackEvent("begin_checkout",    { salon_name: salon }),
  bookingCompleted:  (ref, amount) => trackEvent("purchase",     { transaction_id: ref, value: amount, currency: "INR" }),
  paymentInitiated:  (ref)    => trackEvent("payment_initiated", { booking_ref: ref }),
  addToCart:         (product) => trackEvent("add_to_cart",      { item_name: product }),
  courseEnrolled:    (title)   => trackEvent("course_enrolled",  { course_title: title }),
  reviewSubmitted:   (salon)  => trackEvent("review_submitted", { salon_name: salon }),
};

// ── Mixpanel ─────────────────────────────────────────────────────
const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;

export function initMixpanel() {
  if (!MIXPANEL_TOKEN) return;

  (function (f, b) {
    if (!b.__SV) {
      let e, g, i, h;
      window.mixpanel = b;
      b._i = [];
      b.init = function (e, f, c) {
        function g(a, d) {
          const b = d.split(".");
          b.length === 2 && ((a = a[b[0]]), (d = b[1]));
          a[d] = function () { a.push([d].concat(Array.prototype.slice.call(arguments, 0))); };
        }
        let a = b;
        typeof c !== "undefined" ? (a = b[c] = []) : (c = "mixpanel");
        a.people = a.people || [];
        a.toString = function (a) { let d = "mixpanel"; c !== "mixpanel" && (d += "." + c); a || (d += " (stub)"); return d; };
        a.people.toString = function () { return a.toString(1) + ".people (stub)"; };
        i = "disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" ");
        for (h = 0; h < i.length; h++) g(a, i[h]);
        b._i.push([e, f, c]);
      };
      b.__SV = 1.2;
      e = f.createElement("script");
      e.type = "text/javascript";
      e.async = true;
      e.src = "https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";
      g = f.getElementsByTagName("script")[0];
      g.parentNode.insertBefore(e, g);
    }
  })(document, window.mixpanel || []);

  window.mixpanel.init(MIXPANEL_TOKEN, { track_pageview: true, persistence: "localStorage" });
}

export function identifyUser(user) {
  if (window.mixpanel && user?.id) {
    window.mixpanel.identify(String(user.id));
    window.mixpanel.people.set({
      $name: user.name,
      $email: user.email,
      role: user.role,
      membership_tier: user.membership_tier,
    });
  }
}

// ── Sentry ───────────────────────────────────────────────────────
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
let _sentryModule = null;

export async function initSentry() {
  if (!SENTRY_DSN) return;
  try {
    const Sentry = await import("@sentry/react");
    Sentry.init({
      dsn: SENTRY_DSN,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
      ],
      tracesSampleRate:       Number(import.meta.env.VITE_SENTRY_TRACES_RATE || 0.2),
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      environment: import.meta.env.MODE,
      release:     import.meta.env.VITE_APP_VERSION || undefined,
    });
    _sentryModule = Sentry;
    // Hand the instance to window.Sentry so ErrorBoundary + other
    // non-module code paths can forward exceptions without an import.
    if (typeof window !== "undefined") {
      window.Sentry = Sentry;
    }
  } catch {
    // Sentry SDK not installed — skip silently
  }
}

/** Forward an exception to Sentry if it's been initialised; no-op otherwise. */
export function captureException(err, context = {}) {
  if (_sentryModule) {
    _sentryModule.captureException(err, { extra: context });
  }
}

/** Attach user identity to future Sentry events — call on login. */
export function setSentryUser(user) {
  if (!_sentryModule) return;
  if (!user) {
    _sentryModule.setUser(null);
    return;
  }
  _sentryModule.setUser({
    id:       String(user.id),
    username: user.name,
    email:    user.email || undefined,
  });
}

// ── Init all ─────────────────────────────────────────────────────
export function initAnalytics() {
  initGA4();
  initMixpanel();
  initSentry();
}
